import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import { ImpactStyle } from '@capacitor/haptics';
import { Session } from '@mindly/ui-components';
import { useSessionRatingAnalytics } from '../../shared';
import { userSelector } from '../../redux/user/selector';
import {
  selectSessionForReview,
  selectSubscriptionSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.selector';
import {
  setSessionForReview,
  setSubscriptionSessionReviewModalIsOpen,
} from '../../redux/consultationReviews/consultationsReviews.reducer';
import { createConsultationReview } from '../../redux/consultationReviews/consultationsReviews.thunk';
import { updateConsultationBy } from '../../redux/consultations/reducer';
import {
  createReview as createSpecialistReview,
  fetchClientSpecialistsReviews,
} from '../../redux/specialistReviews/thunk';
import { isClientSpecialistReviewExistsSelector } from '../../redux/specialistReviews/selector';
import { getConsultationByID } from '../../api/consultation';
import { ReviewSubscriptionSessionFeatureTempo } from '../SessionReviewsFeature/ReviewSubscriptionSessionFeature';
import { mergeSession } from 'src/shared/utils/manageSubscriptionSessionReview';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';

const ReviewSubscriptionSessionFeature: React.FC = () => {
  const dispatch = useDispatch();

  const [specialistReviewsLoading, setSpecialistReviewsLoading] = useState(false);
  const isOpen = useSelector(selectSubscriptionSessionReviewModalIsOpen);
  const client = useSelector(userSelector);
  const session: Session = useSelector(selectSessionForReview) as Session;
  const isSpecialistReviewExists = useSelector(isClientSpecialistReviewExistsSelector(session?.psychologist_id ?? ''));

  const ratingAnalyticsTracker = useSessionRatingAnalytics(session);

  useEffect(() => {
    loadReviews();
  }, [session?.id]);

  const loadReviews = async () => {
    try {
      await setSpecialistReviewsLoading(true);
      await dispatch(fetchClientSpecialistsReviews());
    } finally {
      setSpecialistReviewsLoading(false);
    }
  };

  if (!session || !session.subscription_id) {
    return null;
  }

  const handleClose = () => {
    mergeSession(session?.id, {
      isOpen: false,
      sessionStartTimestamp: DateTime.local().toMillis(),
      userId: client?.id ?? '',
    });
    dispatch(setSessionForReview(null));
    dispatch(setSubscriptionSessionReviewModalIsOpen(false));
  };

  const handleSubmitSessionReview = async ({
    rate,
    reasons,
    comment,
  }: {
    rate: number;
    reasons?: string[];
    comment?: string;
  }) => {
    await dispatch(
      createConsultationReview({
        text_of_review: comment ?? '',
        stars_count: rate,
        client_id: client?.id ?? '',
        reasons: reasons ?? [],
        client_name: `${client?.display_name} ${client?.last_name}`,
        client_avatar: client?.avatar ?? '',
        created_at: DateTime.utc().toMillis(),
        specialist_id: session?.psychologist_id ?? '',
        consultation_id: session?.id ?? '',
      })
    ).unwrap();

    const updatedSession = await getConsultationByID(session?.id ?? '');
    if (updatedSession.session_review) {
      dispatch(
        updateConsultationBy({
          field: 'id',
          value: session?.id ?? '',
          data: {
            session_review: updatedSession.session_review,
          },
        })
      );
    }

    if (isSpecialistReviewExists) {
      handleClose();
    }
  };

  const handleSubmitSpecialistReview = async ({ rate, comment }: { rate: number; comment?: string }) => {
    await dispatch(
      createSpecialistReview({
        specialistID: session?.psychologist_id ?? '',
        clientID: client?.id ?? '',
        textOfReview: comment ?? '',
        starsCount: rate,
      })
    );

    await dispatch(fetchClientSpecialistsReviews());

    ratingAnalyticsTracker.trackRatingPublicReviewSubmitted();

    handleClose();
  };

  return (
    <ReviewSubscriptionSessionFeatureTempo
      key={session?.id}
      isOpen={isOpen}
      specialistName={session?.psychologist_name ?? ''}
      specialistAvatar={session?.psychologist_avatar ?? ''}
      specialistReviewNeeded={!specialistReviewsLoading && !isSpecialistReviewExists}
      sessionReviewProps={{
        specialistName: session?.psychologist_name ?? '',
        onModalAppeared: () => ratingAnalyticsTracker.trackRatingScreenViewed(),
        onStarsClick: (value: any) => {
          ratingAnalyticsTracker.trackRatingButtonClicked(value);
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        },
        onReasonClick: (value: any, reasons: any) => ratingAnalyticsTracker.trackReasonTagSelected(value, reasons),
        onCommentBlur: (value: any, reasons: any, comment: any) =>
          ratingAnalyticsTracker.trackCommentBlur(value, reasons, comment),
        onSubmit: handleSubmitSessionReview,
        onContinueClick: () => {
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        },
      }}
      specialistReviewProps={{
        specialistName: session?.psychologist_name ?? '',
        specialistAvatar: session?.psychologist_avatar ?? '',
        onModalAppeared: () => ratingAnalyticsTracker.trackRatingPublicScreenViewed(),
        onStarsClick: (value: any) => {
          ratingAnalyticsTracker.trackRatingPublicRateClicked(value);
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        },
        onSubmit: handleSubmitSpecialistReview,
        onContinueClick: () => {
          isomorficHapticsImpact({ style: ImpactStyle.Light });
        },
      }}
      onClose={handleClose}
      onCloseClick={() => ratingAnalyticsTracker.trackRatingButtonClosedClicked()}
    />
  );
};

export default ReviewSubscriptionSessionFeature;
